import { jsPDF } from "jspdf";
import { Preventivo, PreventivoRoute, PreventivoTappa } from "./Model";
import { STATUS_LABELS } from "../pages/Preventivi";

// Mirrors gocicero_flutter/lib/features/quote/domain/quote_pdf.dart as
// closely as jsPDF allows, so the preventivo PDF downloaded from the admin
// panel looks the same as the one customers get from the app: same brand
// font, same colors, same section layout. Reference data (venue names,
// drink/food/game labels) comes from public/quote/locali.json, a copy of
// the Flutter app's bundled assets/quote/locali.json — the PDF only reads
// venues, never POIs, same as the Dart version.

const BRAND: [number, number, number] = [255, 107, 53];
const BRAND_DARK: [number, number, number] = [229, 90, 37];
const LABEL: [number, number, number] = [28, 28, 30];
const LABEL_SECONDARY: [number, number, number] = [108, 108, 112];
const LABEL_TERTIARY: [number, number, number] = [174, 174, 178];
const SEPARATOR: [number, number, number] = [229, 229, 234];
const BG_SECONDARY: [number, number, number] = [245, 245, 247];
const WARNING: [number, number, number] = [255, 149, 0];
const WHITE: [number, number, number] = [255, 255, 255];

const OCCASION_LABELS: Record<string, string> = {
    compleanno: "Compleanno",
    laurea: "Laurea",
    addiocelibato: "Addio al Celibato",
    addionubilato: "Addio al Nubilato",
    altro: "Altro",
};

interface RefVenue {
    id: string;
    name: string;
    address: string;
    games: { type: string; label: string }[];
    consumazioni: { type: "drink" | "food"; variant: string; label: string }[];
}

let venuesPromise: Promise<RefVenue[]> | null = null;
function loadVenues(): Promise<RefVenue[]> {
    if (!venuesPromise) {
        venuesPromise = fetch("/quote/locali.json")
            .then((r) => r.json())
            .then((j) => (j.venues || []) as RefVenue[])
            .catch(() => []);
    }
    return venuesPromise;
}

function fetchDataUrl(url: string): Promise<string> {
    return fetch(url)
        .then((r) => r.blob())
        .then(
            (blob) =>
                new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                })
        );
}

let logoPromise: Promise<string> | null = null;
let regularFontPromise: Promise<string> | null = null;
let boldFontPromise: Promise<string> | null = null;

function loadLogo(): Promise<string> {
    if (!logoPromise) logoPromise = fetchDataUrl("/quote/app_icon.png");
    return logoPromise;
}
function loadRegularFont(): Promise<string> {
    if (!regularFontPromise) regularFontPromise = fetchDataUrl("/quote/fonts/BeVietnamPro-Regular.ttf");
    return regularFontPromise;
}
function loadBoldFont(): Promise<string> {
    if (!boldFontPromise) boldFontPromise = fetchDataUrl("/quote/fonts/BeVietnamPro-Bold.ttf");
    return boldFontPromise;
}

function base64Body(dataUrl: string): string {
    return dataUrl.substring(dataUrl.indexOf(",") + 1);
}

function occasionLabel(value?: string | null): string {
    if (!value) return "—";
    return OCCASION_LABELS[value] ?? value;
}

function formatEventDate(isoDate?: string | null): string {
    if (!isoDate || isoDate.length < 10) return "—";
    return `${isoDate.substring(8, 10)}/${isoDate.substring(5, 7)}/${isoDate.substring(0, 4)}`;
}

function trimZero(v: number): string {
    return v === Math.round(v) ? String(Math.round(v)) : v.toFixed(2);
}

function setColor(doc: jsPDF, c: [number, number, number]) {
    doc.setTextColor(c[0], c[1], c[2]);
}
function setFill(doc: jsPDF, c: [number, number, number]) {
    doc.setFillColor(c[0], c[1], c[2]);
}
function setDraw(doc: jsPDF, c: [number, number, number]) {
    doc.setDrawColor(c[0], c[1], c[2]);
}

const MARGIN_X = 36;
const MARGIN_TOP = 32;
const MARGIN_BOTTOM = 32;
const FOOTER_RESERVE = 30;
// Title (11pt) + gap to underline (13) + underline (2.4) + gap to card (8),
// matching drawSectionTitle's own math — kept in sync so a title is never
// stranded alone at the bottom of a page without the card that follows it.
const SECTION_TITLE_H = 13 + 2.4 + 8;

function ensureSpace(doc: jsPDF, y: number, needed: number, pageH: number): number {
    if (y + needed > pageH - MARGIN_BOTTOM - FOOTER_RESERVE) {
        doc.addPage();
        return MARGIN_TOP;
    }
    return y;
}

function findVenue(venues: RefVenue[], id: string): RefVenue | undefined {
    return venues.find((v) => v.id === id);
}

function extrasFor(tappa: PreventivoTappa, venues: RefVenue[]): string[] {
    const venue = findVenue(venues, tappa.id);
    const lines: string[] = [];

    const cons = tappa.consumazioni;
    if (cons?.enabled) {
        const drink = venue?.consumazioni.find((c) => c.variant === cons.drinkVariant);
        const food = venue?.consumazioni.find((c) => c.variant === cons.foodVariant);
        if (drink) lines.push(`Drink: ${drink.label}`);
        if (food) lines.push(`Cibo: ${food.label}`);
    } else if (cons && cons.enabled === false) {
        lines.push("Consumazioni non incluse (a carico degli ospiti nel locale)");
    }

    const selectedGames = tappa.gamification?.selectedGames || [];
    if (selectedGames.length) {
        const labels = selectedGames
            .map((gt) => venue?.games.find((g) => g.type === gt)?.label)
            .filter((l): l is string => !!l);
        if (labels.length) lines.push(`Giochi: ${labels.join(", ")}`);
    }

    return lines;
}

interface Stage {
    badge: string;
    color: [number, number, number];
    tappa: PreventivoTappa;
}

function buildStages(route?: PreventivoRoute): Stage[] {
    if (!route) return [];
    const stages: Stage[] = [];
    if (route.startLocation) stages.push({ badge: "PARTENZA", color: BRAND, tappa: route.startLocation });
    route.steps.forEach((s, i) => stages.push({ badge: `TAPPA ${i + 1}`, color: LABEL_SECONDARY, tappa: s }));
    if (route.endLocation) stages.push({ badge: "ARRIVO", color: WARNING, tappa: route.endLocation });
    return stages;
}

function drawHeader(doc: jsPDF, x: number, y: number, w: number, logoDataUrl: string, p: Preventivo): number {
    const logoSize = 40;
    doc.addImage(logoDataUrl, "PNG", x, y, logoSize, logoSize);

    const textX = x + logoSize + 10;
    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(20);
    setColor(doc, BRAND);
    doc.text("GoCicero", textX, y + 16);

    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(10);
    setColor(doc, LABEL_SECONDARY);
    doc.text("Preventivo esperienza di gioco urbano", textX, y + 30);

    const rightX = x + w;
    let ry = y;

    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(8);
    const statusLabel = STATUS_LABELS[p.status]?.toUpperCase();
    if (statusLabel) {
        const textW = doc.getTextWidth(statusLabel);
        const padH = 8;
        const chipH = 14;
        const chipW = textW + padH * 2;
        setFill(doc, BG_SECONDARY);
        doc.roundedRect(rightX - chipW, ry, chipW, chipH, chipH / 2, chipH / 2, "F");
        setColor(doc, BRAND_DARK);
        doc.text(statusLabel, rightX - padH, ry + chipH / 2 + 2.8, { align: "right" });
        ry += chipH + 4;
    }

    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(8);
    setColor(doc, LABEL_TERTIARY);
    const today = new Date();
    const generatedOn = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;
    doc.text(`Generato il ${generatedOn}`, rightX, ry + 7, { align: "right" });
    ry += 10;

    if (p.id) {
        const ref = `Rif. ${p.id.substring(0, Math.min(8, p.id.length)).toUpperCase()}`;
        doc.text(ref, rightX, ry + 7, { align: "right" });
        ry += 10;
    }

    const rowBottom = Math.max(y + logoSize, ry);
    const sepY = rowBottom + 14;
    setDraw(doc, SEPARATOR);
    doc.setLineWidth(1.2);
    doc.line(x, sepY, x + w, sepY);
    return sepY + 16;
}

function drawFooter(doc: jsPDF, pageNumber: number, pageCount: number, pageW: number, pageH: number) {
    const y = pageH - MARGIN_BOTTOM + 4;
    setDraw(doc, SEPARATOR);
    doc.setLineWidth(0.8);
    doc.line(MARGIN_X, y, pageW - MARGIN_X, y);
    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(8);
    setColor(doc, LABEL_TERTIARY);
    doc.text("www.gocicero.it", MARGIN_X, y + 12);
    doc.text(`Pagina ${pageNumber} di ${pageCount}`, pageW - MARGIN_X, y + 12, { align: "right" });
}

function drawSectionTitle(doc: jsPDF, text: string, x: number, y: number): number {
    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(11);
    setColor(doc, BRAND);
    doc.text(text.toUpperCase(), x, y + 9);
    setFill(doc, BRAND);
    doc.rect(x, y + 13, 26, 2.4, "F");
    return y + 13 + 2.4 + 8;
}

const FIELD_GRID_PADDING = 12;
const FIELD_W = 110;
const FIELD_GAP_X = 24;
const FIELD_ROW_GAP_Y = 10;
const FIELD_H = 19.5;
const FIELD_LABEL_H = 7.5;

// Shared by "Dettagli Esperienza" and "Dati Cliente" — both are a grid of
// label/value pairs, same as _detailField in quote_pdf.dart. Event-info
// values (participant count, a date, ...) are always short and safe to pack
// FIELD_W-wide across several columns; contact values (an email address, in
// particular) are not — `stacked` switches to one full-width field per row,
// with the value wrapped so a long email/phone can never bleed into the
// next field's column.
function computeFieldGridHeight(doc: jsPDF, fields: [string, string][], w: number, stacked = false): number {
    const innerW = w - FIELD_GRID_PADDING * 2;
    if (stacked) {
        doc.setFont("BeVietnamPro", "bold");
        doc.setFontSize(11);
        let h = 0;
        fields.forEach(([, value], i) => {
            const lines = doc.splitTextToSize(value, innerW) as string[];
            h += FIELD_LABEL_H + 1 + lines.length * 13;
            if (i < fields.length - 1) h += FIELD_ROW_GAP_Y;
        });
        return FIELD_GRID_PADDING * 2 + h;
    }
    const cols = Math.max(1, Math.floor((innerW + FIELD_GAP_X) / (FIELD_W + FIELD_GAP_X)));
    const rows = Math.ceil(fields.length / cols);
    return FIELD_GRID_PADDING * 2 + rows * FIELD_H + (rows - 1) * FIELD_ROW_GAP_Y;
}

function drawFieldGrid(doc: jsPDF, fields: [string, string][], x: number, y: number, w: number, stacked = false): number {
    const innerW = w - FIELD_GRID_PADDING * 2;
    const cardH = computeFieldGridHeight(doc, fields, w, stacked);

    setFill(doc, BG_SECONDARY);
    doc.roundedRect(x, y, w, cardH, 10, 10, "F");

    if (stacked) {
        let fy = y + FIELD_GRID_PADDING;
        fields.forEach(([label, value]) => {
            const fx = x + FIELD_GRID_PADDING;
            doc.setFont("BeVietnamPro", "normal");
            doc.setFontSize(7.5);
            setColor(doc, LABEL_SECONDARY);
            doc.text(label, fx, fy + FIELD_LABEL_H);
            doc.setFont("BeVietnamPro", "bold");
            doc.setFontSize(11);
            setColor(doc, LABEL);
            const lines = doc.splitTextToSize(value, innerW) as string[];
            lines.forEach((line, i) => doc.text(line, fx, fy + FIELD_LABEL_H + 1 + 11 + i * 13));
            fy += FIELD_LABEL_H + 1 + lines.length * 13 + FIELD_ROW_GAP_Y;
        });
        return y + cardH;
    }

    const cols = Math.max(1, Math.floor((innerW + FIELD_GAP_X) / (FIELD_W + FIELD_GAP_X)));
    fields.forEach(([label, value], i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const fx = x + FIELD_GRID_PADDING + col * (FIELD_W + FIELD_GAP_X);
        const fy = y + FIELD_GRID_PADDING + row * (FIELD_H + FIELD_ROW_GAP_Y);
        doc.setFont("BeVietnamPro", "normal");
        doc.setFontSize(7.5);
        setColor(doc, LABEL_SECONDARY);
        doc.text(label, fx, fy + FIELD_LABEL_H);
        doc.setFont("BeVietnamPro", "bold");
        doc.setFontSize(11);
        setColor(doc, LABEL);
        doc.text(value, fx, fy + FIELD_LABEL_H + 1 + 11);
    });

    return y + cardH;
}

function eventInfoFields(p: Preventivo): [string, string][] {
    return [
        ["PARTECIPANTI", String(p.participants ?? 0)],
        ["DATA", formatEventDate(p.event_date)],
        ["DURATA", p.duration || "—"],
        ["OCCASIONE", occasionLabel(p.occasion)],
        ["ZONA", p.zone || "—"],
    ];
}

function contactFields(p: Preventivo): [string, string][] {
    const fullName = `${p.first_name || ""} ${p.last_name || ""}`.trim();
    return [
        ["CLIENTE", fullName || "—"],
        ["EMAIL", p.email || "—"],
        ["TELEFONO", p.phone || "—"],
    ];
}

function measureStage(doc: jsPDF, stage: Stage, venues: RefVenue[], w: number) {
    const innerW = w - 15 - 12;
    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(11.5);
    const nameLines = doc.splitTextToSize(stage.tappa.name || "", innerW) as string[];

    const venue = findVenue(venues, stage.tappa.id);
    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(8.5);
    const addressLines = venue?.address ? (doc.splitTextToSize(venue.address, innerW) as string[]) : [];

    const extras = extrasFor(stage.tappa, venues);
    doc.setFontSize(9);
    const extraLineArrays = extras.map((e) => doc.splitTextToSize(e, innerW) as string[]);

    let h = 8 + 8 + 2 + nameLines.length * 13;
    if (addressLines.length) h += addressLines.length * 10;
    extraLineArrays.forEach((arr) => (h += 2 + arr.length * 10));
    h += 8;

    return { height: h, nameLines, addressLines, extraLineArrays };
}

function drawStageCard(doc: jsPDF, stage: Stage, venues: RefVenue[], x: number, y: number, w: number): number {
    const { height, nameLines, addressLines, extraLineArrays } = measureStage(doc, stage, venues, w);

    setFill(doc, WHITE);
    doc.roundedRect(x, y, w, height, 8, 8, "F");
    setFill(doc, stage.color);
    doc.rect(x, y, 3, height, "F");

    let cy = y + 8;
    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(8);
    setColor(doc, stage.color);
    doc.text(stage.badge, x + 15, cy + 6);
    cy += 8 + 2;

    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(11.5);
    setColor(doc, LABEL);
    nameLines.forEach((line) => {
        doc.text(line, x + 15, cy + 9);
        cy += 13;
    });

    if (addressLines.length) {
        doc.setFont("BeVietnamPro", "normal");
        doc.setFontSize(8.5);
        setColor(doc, LABEL_SECONDARY);
        addressLines.forEach((line) => {
            doc.text(line, x + 15, cy + 7);
            cy += 10;
        });
    }

    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(9);
    setColor(doc, LABEL_SECONDARY);
    extraLineArrays.forEach((arr) => {
        cy += 2;
        arr.forEach((line) => {
            doc.text(line, x + 15, cy + 7);
            cy += 10;
        });
    });

    return y + height + 6;
}

const GAME_TYPE_LABELS: [keyof NonNullable<PreventivoRoute["gamePreferences"]>, string][] = [
    ["qrCodeHunt", "Caccia al QR Code"],
    ["quiz", "Quiz"],
    ["geoCache", "GeoCache"],
    ["sfide", "Sfide"],
];

const GAM_CHIP_PADDING = 12;
const GAM_CHIP_PAD_H = 10;
const GAM_CHIP_H = 21;
const GAM_CHIP_GAP = 6;

interface GamificationLayout {
    height: number;
    rows: string[][];
}

// Single source of truth for the chip-wrapping row layout — used both to
// measure the card's real height (for pagination) and to draw it, so the
// two can never drift apart.
function computeGamificationLayout(doc: jsPDF, prefs: PreventivoRoute["gamePreferences"], w: number): GamificationLayout {
    const labels = GAME_TYPE_LABELS.filter(([key]) => !!prefs?.[key]).map(([, label]) => label);
    if (!labels.length) {
        return { height: GAM_CHIP_PADDING * 2 + 12, rows: [] };
    }

    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(9);
    const innerW = w - GAM_CHIP_PADDING * 2;

    const rows: string[][] = [[]];
    let rowWidth = 0;
    labels.forEach((label) => {
        const chipW = doc.getTextWidth(label) + GAM_CHIP_PAD_H * 2;
        if (rowWidth > 0 && rowWidth + GAM_CHIP_GAP + chipW > innerW) {
            rows.push([]);
            rowWidth = 0;
        }
        rows[rows.length - 1].push(label);
        rowWidth += (rowWidth > 0 ? GAM_CHIP_GAP : 0) + chipW;
    });

    const height = GAM_CHIP_PADDING * 2 + rows.length * GAM_CHIP_H + (rows.length - 1) * GAM_CHIP_GAP;
    return { height, rows };
}

function drawGamificationCard(doc: jsPDF, layout: GamificationLayout, x: number, y: number, w: number): number {
    setFill(doc, BG_SECONDARY);
    doc.roundedRect(x, y, w, layout.height, 10, 10, "F");

    if (!layout.rows.length) {
        doc.setFont("BeVietnamPro", "normal");
        doc.setFontSize(10.5);
        setColor(doc, LABEL_SECONDARY);
        doc.text("Nessun gioco di gamification selezionato.", x + GAM_CHIP_PADDING, y + GAM_CHIP_PADDING + 8);
        return y + layout.height;
    }

    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(9);
    let cy = y + GAM_CHIP_PADDING;
    layout.rows.forEach((row) => {
        let cx = x + GAM_CHIP_PADDING;
        row.forEach((label) => {
            const chipW = doc.getTextWidth(label) + GAM_CHIP_PAD_H * 2;
            setFill(doc, WHITE);
            setDraw(doc, SEPARATOR);
            doc.setLineWidth(0.8);
            doc.roundedRect(cx, cy, chipW, GAM_CHIP_H, 11, 11, "FD");
            setColor(doc, LABEL);
            doc.text(label, cx + chipW / 2, cy + GAM_CHIP_H / 2 + 3, { align: "center" });
            cx += chipW + GAM_CHIP_GAP;
        });
        cy += GAM_CHIP_H + GAM_CHIP_GAP;
    });

    return y + layout.height;
}

const NOTES_PADDING = 12;

interface NotesLayout {
    height: number;
    lines: string[];
}

function computeNotesLayout(doc: jsPDF, notes: string, w: number): NotesLayout {
    const innerW = w - NOTES_PADDING * 2;
    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(10.5);
    const lines = doc.splitTextToSize(notes.trim(), innerW) as string[];
    return { height: NOTES_PADDING * 2 + lines.length * 13, lines };
}

function drawNotesCard(doc: jsPDF, layout: NotesLayout, x: number, y: number, w: number): number {
    setFill(doc, BG_SECONDARY);
    doc.roundedRect(x, y, w, layout.height, 10, 10, "F");
    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(10.5);
    setColor(doc, LABEL);
    layout.lines.forEach((line, i) => doc.text(line, x + NOTES_PADDING, y + NOTES_PADDING + 9 + i * 13));

    return y + layout.height;
}

const PRICING_PADDING = 12;
const PRICING_ROW_H = 15;
const PRICING_TOTAL_BOX_H = 66;
const PRICING_LEGAL_LINES = [
    "Tutte le imposte e tasse sono incluse.",
    "Il presente preventivo ha una validità di 30 giorni.",
    "In caso di maltempo improvviso non è previsto alcun rimborso. Sarà valutata la migliore soluzione condivisa. Se il maltempo è previsto almeno 5 giorni prima, il Cliente può recedere o richiedere il rinvio dell'evento.",
];

interface PricingLayout {
    height: number;
    legalWrapped: string[][];
}

function computePricingLayout(doc: jsPDF, p: Preventivo, w: number): PricingLayout {
    const innerW = w - PRICING_PADDING * 2;
    const breakdown = p.breakdown || [];
    const breakdownH = breakdown.length * PRICING_ROW_H;

    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(7);
    const legalWrapped = PRICING_LEGAL_LINES.map((l) => doc.splitTextToSize(l, innerW) as string[]);
    const legalH = legalWrapped.reduce((acc, arr) => acc + arr.length * 9, 0);

    const height = PRICING_PADDING * 2 + breakdownH + 8 + 1.2 + 8 + PRICING_TOTAL_BOX_H + 10 + legalH;
    return { height, legalWrapped };
}

function drawPricingCard(doc: jsPDF, layout: PricingLayout, p: Preventivo, x: number, y: number, w: number): number {
    const padding = PRICING_PADDING;
    const innerW = w - padding * 2;
    const breakdown = p.breakdown || [];
    const totalPrice = p.total_price ?? 0;
    const perPerson = p.participants > 0 ? totalPrice / p.participants : 0;
    const totalBoxH = PRICING_TOTAL_BOX_H;

    setFill(doc, BG_SECONDARY);
    doc.roundedRect(x, y, w, layout.height, 10, 10, "F");

    let cy = y + padding;
    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(9.5);
    breakdown.forEach((item) => {
        setColor(doc, LABEL);
        doc.text(item.label, x + padding, cy + 9);
        doc.setFont("BeVietnamPro", "bold");
        doc.text(`${trimZero(item.amount)}€`, x + w - padding, cy + 9, { align: "right" });
        doc.setFont("BeVietnamPro", "normal");
        cy += PRICING_ROW_H;
    });

    cy += 4;
    setDraw(doc, SEPARATOR);
    doc.setLineWidth(1.2);
    doc.line(x + padding, cy, x + w - padding, cy);
    cy += 8;

    setFill(doc, BRAND);
    doc.roundedRect(x + padding, cy, innerW, totalBoxH, 10, 10, "F");
    setColor(doc, WHITE);
    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(8.5);
    doc.text("Prezzo per persona", x + padding + 12, cy + 20);
    doc.setFont("BeVietnamPro", "bold");
    doc.setFontSize(20);
    doc.text(`${perPerson.toFixed(2)}€`, x + padding + 12, cy + 44);

    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(8.5);
    doc.text(`Totale: ${trimZero(totalPrice)}€`, x + w - padding - 12, cy + 34, { align: "right" });
    doc.text(`${p.participants} persone`, x + w - padding - 12, cy + 44, { align: "right" });

    cy += totalBoxH + 10;
    doc.setFont("BeVietnamPro", "normal");
    doc.setFontSize(7.5);
    setColor(doc, LABEL_TERTIARY);
    doc.text(PRICING_LEGAL_LINES[0], x + padding, cy + 6);
    cy += 9;
    doc.text(PRICING_LEGAL_LINES[1], x + padding, cy + 6);
    cy += 9;
    doc.setFontSize(7);
    layout.legalWrapped[2].forEach((line) => {
        doc.text(line, x + padding, cy + 5);
        cy += 9;
    });

    return y + layout.height;
}

export async function downloadPreventivoPdf(p: Preventivo): Promise<void> {
    const [venues, logoDataUrl, regularFontUrl, boldFontUrl] = await Promise.all([
        loadVenues(),
        loadLogo(),
        loadRegularFont(),
        loadBoldFont(),
    ]);

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.addFileToVFS("BeVietnamPro-Regular.ttf", base64Body(regularFontUrl));
    doc.addFont("BeVietnamPro-Regular.ttf", "BeVietnamPro", "normal");
    doc.addFileToVFS("BeVietnamPro-Bold.ttf", base64Body(boldFontUrl));
    doc.addFont("BeVietnamPro-Bold.ttf", "BeVietnamPro", "bold");
    doc.setFont("BeVietnamPro", "normal");

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const contentW = pageW - MARGIN_X * 2;

    let y = drawHeader(doc, MARGIN_X, MARGIN_TOP, contentW, logoDataUrl, p);

    const detailFields = eventInfoFields(p);
    y = ensureSpace(doc, y, SECTION_TITLE_H + computeFieldGridHeight(doc, detailFields, contentW), pageH);
    y = drawSectionTitle(doc, "Dettagli Esperienza", MARGIN_X, y);
    y = drawFieldGrid(doc, detailFields, MARGIN_X, y, contentW);
    y += 8;

    // Stacked (one field per row, wrapped) — a fixed-width grid column can't
    // safely fit an email address without it bleeding into the next field.
    const custFields = contactFields(p);
    y = ensureSpace(doc, y, SECTION_TITLE_H + computeFieldGridHeight(doc, custFields, contentW, true), pageH);
    y = drawSectionTitle(doc, "Dati Cliente", MARGIN_X, y);
    y = drawFieldGrid(doc, custFields, MARGIN_X, y, contentW, true);
    y += 8;

    const stages = buildStages(p.route);
    if (!stages.length) {
        y = ensureSpace(doc, y, SECTION_TITLE_H + 16, pageH);
        y = drawSectionTitle(doc, "Il tuo Percorso", MARGIN_X, y);
        doc.setFont("BeVietnamPro", "normal");
        doc.setFontSize(10);
        setColor(doc, LABEL_SECONDARY);
        doc.text("Nessuna tappa selezionata.", MARGIN_X, y + 8);
        y += 16;
    } else {
        // Keep the section title glued to its first stage card — measure
        // that card up front so a page break (if needed) happens before the
        // title, never leaving it orphaned at the bottom of a page.
        const firstStageHeight = measureStage(doc, stages[0], venues, contentW).height;
        y = ensureSpace(doc, y, SECTION_TITLE_H + firstStageHeight, pageH);
        y = drawSectionTitle(doc, "Il tuo Percorso", MARGIN_X, y);
        for (const stage of stages) {
            const { height } = measureStage(doc, stage, venues, contentW);
            y = ensureSpace(doc, y, height, pageH);
            y = drawStageCard(doc, stage, venues, MARGIN_X, y, contentW);
        }
    }
    y += 8;

    const gamLayout = computeGamificationLayout(doc, p.route?.gamePreferences, contentW);
    y = ensureSpace(doc, y, SECTION_TITLE_H + gamLayout.height, pageH);
    y = drawSectionTitle(doc, "Gamification", MARGIN_X, y);
    y = drawGamificationCard(doc, gamLayout, MARGIN_X, y, contentW);
    y += 8;

    if (p.additional_notes && p.additional_notes.trim()) {
        const notesLayout = computeNotesLayout(doc, p.additional_notes, contentW);
        y = ensureSpace(doc, y, SECTION_TITLE_H + notesLayout.height, pageH);
        y = drawSectionTitle(doc, "Note Aggiuntive", MARGIN_X, y);
        y = drawNotesCard(doc, notesLayout, MARGIN_X, y, contentW);
        y += 8;
    }

    const pricingLayout = computePricingLayout(doc, p, contentW);
    y = ensureSpace(doc, y, SECTION_TITLE_H + pricingLayout.height, pageH);
    y = drawSectionTitle(doc, "Riepilogo Prezzi", MARGIN_X, y);
    y = drawPricingCard(doc, pricingLayout, p, MARGIN_X, y, contentW);

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        drawFooter(doc, i, pageCount, pageW, pageH);
    }

    // event_date can come back as a full ISO datetime ("...T00:00:00.000Z")
    // even though only the calendar date matters here — always trim to it.
    const datePart = (p.event_date || new Date().toISOString()).substring(0, 10);
    doc.save(`preventivo-gocicero-${datePart}.pdf`);
}
